import { computed, ref } from 'vue'

import { useDebounceFn } from './useDebounceFn'

/**
 * A hook to manage loading states with a delay.
 * @param options.initialValue - The initial value of the loading state.
 * @default true
 * @param options.delayLoadingMs - The delay in milliseconds before the loading state is set to true.
 * @default 300
 */
export function useLoading(
  options: {
    /** The initial value of the loading state. @default true */
    initialValue?: boolean
    /** The delay in milliseconds before the loading state is set to true. @default 300 */
    delayLoadingMs?: number
  } = {}
) {
  /** The loading state. */
  const loading = ref(options.initialValue ?? true)

  /** The delayed loading state. */
  const delayedLoading = ref(false)

  const isLoading = computed(() => {
    return loading.value || delayedLoading.value
  })

  const isDelayedLoading = computed(() => {
    return delayedLoading.value
  })

  /**
   * @description Sets the delayed loading state.
   * @param value - The value to set the delayed loading state to.
   */
  const setDelayedLoading = useDebounceFn((value: boolean) => {
    delayedLoading.value = value
  }, options.delayLoadingMs ?? 300)

  /**
   * @description Sets the loading state.
   * @param value - The value to set the loading state to.
   */
  function setLoading(value: boolean) {
    if (value) {
      loading.value = true
      setDelayedLoading(true)
    } else {
      loading.value = false
      setDelayedLoading(false)
    }
  }

  /**
   * @description Returns the loading state.
   */
  return {
    /** Sets the loading state and the delayed loading state. */
    setLoading,
    /** The loading state. */
    isLoading,
    /** The delayed loading state. */
    isDelayedLoading
  }
}
